import { useCallback, useState } from "react";
import Cropper, { Area } from "react-easy-crop";

interface Props {
  /** data: URL of the image being cropped. */
  src: string;
  /** width / height aspect ratio (default 16:10 to match the form preview). */
  aspect?: number;
  /** Max longest-side of the resulting JPEG. */
  outputMax?: number;
  /** JPEG quality 0–1. */
  outputQuality?: number;
  onCancel: () => void;
  onConfirm: (croppedDataUrl: string) => void;
}

async function cropToDataUrl(
  src: string,
  area: Area,
  outputMax: number,
  quality: number,
): Promise<string> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    // ImageKit serves with CORS headers — required so the canvas isn't tainted.
    if (!src.startsWith("data:")) i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("decode failed"));
    i.src = src;
  });

  // Cap output at outputMax on the longest side of the cropped region.
  const longest = Math.max(area.width, area.height);
  const scale = longest > outputMax ? outputMax / longest : 1;
  const w = Math.round(area.width * scale);
  const h = Math.round(area.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return src;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

const CropImageModal: React.FC<Props> = ({
  src,
  aspect = 16 / 10,
  outputMax = 1280,
  outputQuality = 0.88,
  onCancel,
  onConfirm,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_: Area, pixels: Area) => {
    setAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!areaPixels) return;
    setBusy(true);
    try {
      const out = await cropToDataUrl(src, areaPixels, outputMax, outputQuality);
      onConfirm(out);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink-1000/85 backdrop-blur-sm flex flex-col">
      <header className="px-5 pt-5 pb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="text-white text-sm font-semibold"
        >
          Cancel
        </button>
        <span className="font-display font-bold text-white text-base">Crop image</span>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={busy || !areaPixels}
          className="text-accent-on bg-accent px-4 h-9 rounded-full text-sm font-bold disabled:opacity-50"
        >
          {busy ? "Saving…" : "Done"}
        </button>
      </header>

      <div className="flex-1 relative bg-ink-1000">
        <div className="absolute inset-0">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
            objectFit="contain"
            showGrid
          />
        </div>
      </div>

      <div className="px-6 pt-4 pb-6 bg-ink-1000">
        <div className="flex items-center gap-3">
          <span className="text-white/70 text-xs">−</span>
          <input
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-white"
            aria-label="Zoom"
          />
          <span className="text-white/70 text-xs">+</span>
        </div>
        <p className="mt-2 text-center text-[11px] text-white/50">
          Drag to position · pinch or slide to zoom
        </p>
      </div>
    </div>
  );
};

export default CropImageModal;
